import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { subscribeNewsletter } from '../../services/api';
import SocialLinks from './SocialLinks';
import './Footer.css';

const Footer = () => {
    const currentYear = new Date().getFullYear();
    const [promoEmail, setPromoEmail] = useState('');
    const [promoSubmitted, setPromoSubmitted] = useState(false);
    const [promoError, setPromoError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handlePromoSubmit = async (e) => {
        e.preventDefault();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!promoEmail.trim()) {
            setPromoError('Please enter your email');
            return;
        }
        if (!emailRegex.test(promoEmail)) {
            setPromoError('Please enter a valid email');
            return;
        }

        setIsSubmitting(true);
        setPromoError('');

        try {
            const result = await subscribeNewsletter(promoEmail);
            if (result.success) {
                // Store email and show success
                localStorage.setItem('timbrlux_signup_email', promoEmail);
                setPromoSubmitted(true);
            } else {
                setPromoError(result.message || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            setPromoError('Unable to connect to the server. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Structured data for Organization (SEO/AEO/GEO)
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "LodgingBusiness",
        "name": "TimbrLux Stays",
        "alternateName": "Frost Pine Chalet",
        "description": "Your cozy mountain retreat awaits. Book direct and save.",
        "url": "https://www.timbrluxstays.com",
        "logo": "https://www.timbrluxstays.com/image/logo.svg",
        "image": "https://www.timbrluxstays.com/image/og-image.jpg",
        "telephone": "+1-206-558-8542",
        "email": "vikas@timbrluxstays.com",
        "priceRange": "$$$",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Frost Pine Chalet",
            "addressLocality": "Newry",
            "addressRegion": "ME",
            "postalCode": "04261",
            "addressCountry": "US"
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": "44.5275",
            "longitude": "-70.8011"
        },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5.0",
            "reviewCount": "9",
            "bestRating": "5",
            "worstRating": "1"
        },
        "amenityFeature": [
            { "@type": "LocationFeatureSpecification", "name": "Hot Tub" },
            { "@type": "LocationFeatureSpecification", "name": "Private Sauna" },
            { "@type": "LocationFeatureSpecification", "name": "Game Room" },
            { "@type": "LocationFeatureSpecification", "name": "Mountain Views" },
            { "@type": "LocationFeatureSpecification", "name": "Free WiFi" }
        ],
        "sameAs": [
            "https://instagram.com/timbrluxstays",
            "https://facebook.com/timbrluxstays",
            "https://youtube.com/@timbrluxstays",
            "https://x.com/timbrluxstays",
            "https://linkedin.com/company/timbrluxstays"
        ]
    };

    return (
        <footer
            className="footer"
            role="contentinfo"
            itemScope
            itemType="https://schema.org/WPFooter"
        >
            {/* Schema.org JSON-LD */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
            />

            <div className="footer-container">
                {/* 10% Signup Promo Banner */}
                <div className="footer-promo-banner">
                    {!promoSubmitted ? (
                        <>
                            <div className="footer-promo-text">
                                <span className="footer-promo-badge">NEW GUEST OFFER</span>
                                <p>Sign up and save <strong>10%</strong> on your first stay</p>
                            </div>
                            <form className="footer-promo-form" onSubmit={handlePromoSubmit}>
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    value={promoEmail}
                                    onChange={(e) => { setPromoEmail(e.target.value); setPromoError(''); }}
                                    className={promoError ? 'error' : ''}
                                />
                                <button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? 'Sending...' : 'Get 10% Off'}
                                </button>
                            </form>
                            {promoError && <span className="footer-promo-error">{promoError}</span>}
                        </>
                    ) : (
                        <div className="footer-promo-success">
                            <span>✓</span> Thanks! Check your email for your 10% discount code.
                        </div>
                    )}
                </div>

                {/* Brand Section */}
                <div className="footer-brand" itemScope itemType="https://schema.org/Organization">
                    <span className="footer-logo" itemProp="name">
                        TimbrLux Stays
                    </span>
                    <p className="footer-description" itemProp="description">
                        Your cozy mountain retreat awaits. Book direct and save.
                    </p>
                </div>

                {/* Divider Line */}
                <hr className="footer-divider" aria-hidden="true" />

                {/* Desktop Footer Columns */}
                <div className="footer-columns footer-columns-desktop">
                    {/* Location Column */}
                    <div
                        className="footer-column"
                        itemScope
                        itemType="https://schema.org/Place"
                    >
                        <p className="footer-column-title">Location</p>
                        <address
                            className="footer-column-content"
                            itemProp="address"
                            itemScope
                            itemType="https://schema.org/PostalAddress"
                        >
                            <span itemProp="description">Entire home in </span>
                            <span itemProp="addressLocality">Newry</span>,
                            <span itemProp="addressRegion"> Maine</span>,
                            <span itemProp="addressCountry"> United States</span>
                        </address>
                    </div>

                    {/* Vertical Divider */}
                    <div className="footer-vertical-divider" aria-hidden="true"></div>

                    {/* Quick Links Column */}
                    <nav
                        className="footer-column"
                        aria-label="Footer navigation"
                        itemScope
                        itemType="https://schema.org/SiteNavigationElement"
                    >
                        <p className="footer-column-title">Quick links</p>
                        <ul className="footer-column-content footer-links">
                            <li><Link to="/" itemProp="url"><span itemProp="name">Home</span></Link></li>
                            <li><Link to="/about" itemProp="url"><span itemProp="name">About Us</span></Link></li>
                            <li><Link to="/gallery" itemProp="url"><span itemProp="name">Gallery</span></Link></li>
                            <li><Link to="/blog" itemProp="url"><span itemProp="name">Blog</span></Link></li>
                            <li><Link to="/faqs" itemProp="url"><span itemProp="name">FAQs</span></Link></li>
                        </ul>
                    </nav>

                    {/* Vertical Divider */}
                    <div className="footer-vertical-divider" aria-hidden="true"></div>

                    {/* Social Links Column */}
                    <div className="footer-column">
                        <p className="footer-column-title">Follow us</p>
                        <SocialLinks className="footer-column-content footer-social" />
                    </div>
                </div>

                {/* Mobile Footer Columns - Different Structure */}
                <div className="footer-columns footer-columns-mobile">
                    {/* Location */}
                    <div className="footer-column">
                        <p className="footer-column-title">Location</p>
                        <address className="footer-column-content">
                            Entire home in Newry, Maine, United States
                        </address>
                    </div>

                    {/* Quick Links + Follow Us Row */}
                    <div className="footer-mobile-row">
                        <nav className="footer-column" aria-label="Footer navigation mobile">
                            <p className="footer-column-title">Quick links</p>
                            <ul className="footer-column-content footer-links">
                                <li><Link to="/">Home</Link></li>
                                <li><Link to="/about">About Us</Link></li>
                                <li><Link to="/gallery">Gallery</Link></li>
                                <li><Link to="/blog">Blog</Link></li>
                                <li><Link to="/faqs">FAQs</Link></li>
                            </ul>
                        </nav>

                        <div className="footer-column">
                            <p className="footer-column-title">Follow us</p>
                            <SocialLinks className="footer-column-content footer-social" />
                        </div>
                    </div>
                </div>

                {/* Divider Line */}
                <hr className="footer-divider" aria-hidden="true" />

                {/* Bottom Bar */}
                <div className="footer-bottom">
                    <p className="footer-copyright">
                        <small>Copyright {currentYear} TimbrLux Stays</small>
                    </p>

                    <nav className="footer-legal" aria-label="Legal links">
                        <Link to="/privacy">Privacy Policy</Link>
                        <span aria-hidden="true">|</span>
                        <Link to="/terms">Terms & Condition</Link>
                    </nav>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
